import { FileText, Printer, X } from 'lucide-react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { Button } from '../common/Button';

// Print-only component for card format
const PrintCardContainer = styled.div`
  width: 100%;
  max-width: 80mm;
  margin: 0 auto;
  border-top: 2px dashed #000;
  border-left: 2px dashed #000;
  border-right: 2px dashed #000;
  padding: 2mm;
  font-family: 'Courier New', monospace;
  font-size: 11pt;
  line-height: 1.4;
  color: #000;
  background: white;
  display: none;

  @media print {
    display: block;
    border: none;
    page-break-after: always;
    padding: 0;
  }
`;

const PrintCardHeader = styled.div`
  text-align: center;
  margin-bottom: 2mm;
  border-bottom: 1px dashed #000;
  padding-bottom: 2mm;
  font-weight: bold;

  .table-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2mm;
  }

  .order-number {
    font-size: 12pt;
    font-weight: bold;
  }

  .timestamp {
    font-size: 9pt;
    color: #333;
  }
`;

const PrintCardItems = styled.div`
  margin-bottom: 0;
  padding-bottom: 2mm;

  .items-title {
    font-weight: bold;
    margin-bottom: 2mm;
  }

  .item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1mm;
    font-size: 10pt;

    .qty-name {
      flex: 1;
    }

    .qty {
      margin-right: 2mm;
      font-weight: bold;
    }
  }

  .notes {
    font-size: 9pt;
    font-style: italic;
    margin-left: 4mm;
    color: #555;
  }
`;

const KOTContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  border-left: 5px solid ${({ status }) => {
    switch (status) {
      case 'pending':
        return theme.colors.warning;
      case 'preparing':
        return theme.colors.info;
      case 'ready':
        return theme.colors.success;
      default:
        return theme.colors.border;
    }
  }};
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const KOTHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const KOTNumber = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text};
  margin: 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background-color: ${({ status }) => {
    switch (status) {
      case 'pending':
        return '#FEF3C7';
      case 'preparing':
        return '#DBEAFE';
      case 'ready':
        return '#DCFCE7';
      default:
        return theme.colors.border;
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'pending':
        return '#92400E';
      case 'preparing':
        return '#0C4A6E';
      case 'ready':
        return '#15803D';
      default:
        return theme.colors.textSecondary;
    }
  }};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semiBold};
`;

const KOTDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.sm};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;

  label {
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.textSecondary};
    margin-bottom: 4px;
  }

  span {
    color: ${theme.colors.text};
    font-size: ${theme.typography.fontSize.base};
  }
`;

const ItemsSection = styled.div`
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  flex: 1;
  overflow-y: auto;
`;

const ItemsTitle = styled.h4`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semiBold};
  margin: 0 0 ${theme.spacing.md} 0;
  color: ${theme.colors.text};
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.div`
  flex: 1;
  font-weight: ${theme.typography.fontWeight.semiBold};
  color: ${theme.colors.text};
`;

const ItemQty = styled.div`
  min-width: 50px;
  text-align: center;
  background-color: ${theme.colors.primary}15;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
`;

const SpecialInstructions = styled.div`
  background-color: #FFF3CD;
  border-left: 3px solid #FFB800;
  padding: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: #664D03;
  border-radius: ${theme.borderRadius.sm};
  font-style: italic;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: auto;
  padding-top: ${theme.spacing.md};
  border-top: 2px solid ${theme.colors.border};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const PrintButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  flex: 1;
  font-size: ${theme.typography.fontSize.sm};
  padding: ${theme.spacing.md} ${theme.spacing.sm};
  white-space: nowrap;
`;

// Main KOT Display Component
export const KOTDisplay = ({ kot, onStatusChange, onPrint, onPrintOrder, onClose }) => {
  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <KOTContainer status={kot.status}>
      <KOTHeader>
        <div>
          <KOTNumber>{kot.kotNumber}</KOTNumber>
        </div>
        <StatusBadge status={kot.status}>
          {kot.status.toUpperCase()}
        </StatusBadge>
      </KOTHeader>

      <KOTDetails>
        <DetailItem>
          <label>Order #</label>
          <span>{kot.orderNumber}</span>
        </DetailItem>
        <DetailItem>
          <label>Table / Section</label>
          <span>{kot.tableNumber} / {kot.section}</span>
        </DetailItem>
        <DetailItem>
          <label>Waiter</label>
          <span>{kot.waiterName || 'N/A'}</span>
        </DetailItem>
        <DetailItem>
          <label>Time</label>
          <span>{formatTime(kot.createdAt)}</span>
        </DetailItem>
      </KOTDetails>

      <ItemsSection>
        <ItemsTitle>Items</ItemsTitle>
        {kot.items.map((item, index) => (
          <div key={index}>
            <ItemRow>
              <ItemName>{item.itemName}</ItemName>
              <ItemQty>×{item.quantity}</ItemQty>
            </ItemRow>
            {item.specialInstructions && (
              <SpecialInstructions>
                Note: {item.specialInstructions}
              </SpecialInstructions>
            )}
          </div>
        ))}
      </ItemsSection>

      {kot.notes && (
        <SpecialInstructions>
          Order Notes: {kot.notes}
        </SpecialInstructions>
      )}

      <ActionButtons>
        <PrintButton
          variant="secondary"
          onClick={onPrint}
        >
          <Printer size={16} />
          Print Kitchen Order
        </PrintButton>
        <PrintButton
          variant="accent"
          onClick={onPrintOrder}
        >
          <FileText size={16} />
          Print Bill
        </PrintButton>
        {kot.status !== 'ready' && (
          <PrintButton
            onClick={() => {
              const nextStatus = kot.status === 'pending' ? 'preparing' : 'ready';
              onStatusChange(kot.id, nextStatus);
            }}
            variant="primary"
          >
            {kot.status === 'pending' ? 'Start Preparing' : 'Mark Ready'}
          </PrintButton>
        )}
        {onClose && (
          <PrintButton
            variant="secondary"
            onClick={onClose}
            style={{ padding: '0 10px' }}
          >
            <X size={16} />
          </PrintButton>
        )}
      </ActionButtons>
    </KOTContainer>
  );
};

// Printable KOT Component
const PrintableKOTContainer = styled.div`
  @media print {
    @page {
      size: 80mm auto;
      margin: 0;
      padding: 0;
    }
    margin: 0;
    padding: 0;
    * {
      box-sizing: border-box;
    }
  }

  @media screen {
    max-width: 300px;
    margin: 0 auto;
    padding: 20px;
    background: white;
  }
`;

const PrintableKOT = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #000;
  background: white;
  padding: 15px;

  @media print {
    padding: 10px;
  }
`;

const PrintHeader = styled.div`
  text-align: center;
  margin-bottom: 15px;
  border-bottom: 1px dashed #000;
  padding-bottom: 10px;
`;

const PrintTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const PrintSubtitle = styled.p`
  font-size: 11px;
  margin: 5px 0 0 0;
  color: #333;
`;

const PrintDetails = styled.div`
  font-size: 11px;
  margin-bottom: 10px;
`;

const PrintRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 3px 0;
`;

const ItemsTable = styled.div`
  margin: 10px 0;
  border-top: 1px dashed #000;
  border-bottom: 1px dashed #000;
  padding: 10px 0;
`;

const PrintItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
  font-size: 12px;

  ${({ isQty }) => isQty && `
    font-weight: bold;
    margin-top: 8px;
  `}
`;

export const PrintableKOTComponent = ({ kot }) => {
  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  return (
    <PrintableKOTContainer className="no-print-external">
      <PrintableKOT className="printable-kot">
        <PrintHeader>
          <PrintTitle>KITCHEN ORDER TICKET</PrintTitle>
          <PrintSubtitle>KOT: {kot.kotNumber}</PrintSubtitle>
        </PrintHeader>

        <PrintDetails>
          <PrintRow>
            <span>Order: {kot.orderNumber}</span>
            <span>Time: {formatTime(kot.createdAt)}</span>
          </PrintRow>
          <PrintRow>
            <span>Table: {kot.tableNumber}</span>
            <span>Section: {kot.section}</span>
          </PrintRow>
          {kot.waiterName && (
            <PrintRow>
              <span>Waiter: {kot.waiterName}</span>
            </PrintRow>
          )}
        </PrintDetails>

        <PrintRow style={{ borderBottom: '1px dashed #000', paddingBottom: '10px', marginBottom: '10px' }}>
          <span style={{ fontWeight: 'bold' }}>Items</span>
          <span style={{ fontWeight: 'bold' }}>Qty</span>
        </PrintRow>

        <ItemsTable>
          {kot.items.map((item, index) => (
            <div key={index}>
              <PrintItem>
                <span>{item.itemName}</span>
                <span style={{ fontWeight: 'bold' }}>×{item.quantity}</span>
              </PrintItem>
              {item.specialInstructions && (
                <PrintItem style={{ fontSize: '10px', fontStyle: 'italic', marginLeft: '10px', color: '#666' }}>
                  *{item.specialInstructions}
                </PrintItem>
              )}
            </div>
          ))}
        </ItemsTable>

        {kot.notes && (
          <PrintDetails style={{ marginTop: '10px' }}>
            <PrintRow>
              <span style={{ fontWeight: 'bold' }}>Notes:</span>
            </PrintRow>
            <PrintRow>
              <span>{kot.notes}</span>
            </PrintRow>
          </PrintDetails>
        )}

        <PrintDetails style={{ marginTop: '10px', textAlign: 'center', fontSize: '10px', color: '#666' }}>
          <PrintRow>
            <span>Version: {kot.kotVersion || 1}</span>
          </PrintRow>
        </PrintDetails>
      </PrintableKOT>
    </PrintableKOTContainer>
  );
};

// Printable Order Component (Full Order Details)
const PrintableOrderContainer = styled.div`
  @media print {
    @page {
      size: 80mm auto;
      margin: 0;
      padding: 0;
    }
    margin: 0;
    padding: 0;
    * {
      box-sizing: border-box;
    }
  }

  @media screen {
    max-width: 300px;
    margin: 0 auto;
    padding: 20px;
    background: white;
  }
`;

const PrintableOrder = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #000;
  background: white;
  padding: 15px;

  @media print {
    padding: 10px;
  }
`;

const OrderHeader = styled.div`
  text-align: center;
  margin-bottom: 15px;
  border-bottom: 1px dashed #000;
  padding-bottom: 10px;
`;

const OrderTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const OrderDetails = styled.div`
  font-size: 11px;
  margin-bottom: 10px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 3px 0;
  font-size: 11px;
`;

const OrderItemsTable = styled.div`
  margin: 10px 0;
  border-top: 1px dashed #000;
  border-bottom: 1px dashed #000;
  padding: 10px 0;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
  font-size: 12px;
`;

const ItemDetailLeft = styled.div`
  flex: 1;
`;

const ItemDetailRight = styled.div`
  text-align: right;
  min-width: 60px;
`;

const SummarySection = styled.div`
  margin-top: 15px;
  border-top: 1px dashed #000;
  padding-top: 10px;
  font-size: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 5px 0;
  
  ${({ isBold }) => isBold && `
    font-weight: bold;
    border-top: 1px solid #000;
    padding-top: 5px;
  `}
`;

export const PrintableOrderComponent = ({ kot, orderDetails }) => {
  const formatTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get order details from prop or kot
  const subtotal = orderDetails?.subtotal || 0;
  const gst = orderDetails?.gst || 0;
  const discount = orderDetails?.discount || 0;
  const total = orderDetails?.total || 0;

  return (
    <PrintableOrderContainer className="no-print-external">
      <PrintableOrder className="printable-order">
        <OrderHeader>
          <OrderTitle>ORDER BILL</OrderTitle>
          <div style={{ fontSize: '10px', marginTop: '5px' }}>
            {kot.orderNumber}
          </div>
        </OrderHeader>

        <OrderDetails>
          <DetailRow>
            <span>Date & Time:</span>
            <span>{formatTime(kot.createdAt)}</span>
          </DetailRow>
          <DetailRow>
            <span>Table:</span>
            <span>{kot.tableNumber}</span>
          </DetailRow>
          <DetailRow>
            <span>Section:</span>
            <span>{kot.section}</span>
          </DetailRow>
          {kot.waiterName && (
            <DetailRow>
              <span>Waiter:</span>
              <span>{kot.waiterName}</span>
            </DetailRow>
          )}
        </OrderDetails>

        <DetailRow style={{ fontWeight: 'bold', borderBottom: '1px dashed #000', paddingBottom: '10px', marginBottom: '10px' }}>
          <span>Item</span>
          <div style={{ textAlign: 'right' }}>
            <div>Qty</div>
          </div>
        </DetailRow>

        <OrderItemsTable>
          {kot.items.map((item, index) => (
            <div key={index}>
              <OrderItem>
                <ItemDetailLeft>{item.itemName}</ItemDetailLeft>
                <ItemDetailRight>×{item.quantity}</ItemDetailRight>
              </OrderItem>
              {item.specialInstructions && (
                <OrderItem style={{ fontSize: '10px', fontStyle: 'italic', marginLeft: '10px', color: '#666' }}>
                  <span>*{item.specialInstructions}</span>
                </OrderItem>
              )}
            </div>
          ))}
        </OrderItemsTable>

        {(subtotal > 0 || gst > 0 || discount > 0 || total > 0) && (
          <SummarySection>
            {subtotal > 0 && (
              <SummaryRow>
                <span>Subtotal:</span>
                <span>₹{parseFloat(subtotal).toFixed(2)}</span>
              </SummaryRow>
            )}
            {gst > 0 && (
              <SummaryRow>
                <span>GST:</span>
                <span>₹{parseFloat(gst).toFixed(2)}</span>
              </SummaryRow>
            )}
            {discount > 0 && (
              <SummaryRow>
                <span>Discount:</span>
                <span>-₹{parseFloat(discount).toFixed(2)}</span>
              </SummaryRow>
            )}
            {total > 0 && (
              <SummaryRow isBold>
                <span>TOTAL:</span>
                <span>₹{parseFloat(total).toFixed(2)}</span>
              </SummaryRow>
            )}
          </SummarySection>
        )}

        {kot.notes && (
          <OrderDetails style={{ marginTop: '10px' }}>
            <DetailRow>
              <span style={{ fontWeight: 'bold' }}>Notes:</span>
            </DetailRow>
            <div style={{ fontSize: '11px', marginTop: '5px', whiteSpace: 'pre-wrap' }}>
              {kot.notes}
            </div>
          </OrderDetails>
        )}

        <OrderDetails style={{ marginTop: '15px', textAlign: 'center', fontSize: '10px', borderTop: '1px dashed #000', paddingTop: '10px' }}>
          <div>Thank You!</div>
          <div style={{ marginTop: '5px' }}>KOT: {kot.kotNumber}</div>
        </OrderDetails>
      </PrintableOrder>
    </PrintableOrderContainer>
  );
};

// New component: Print Order Card in receipt format
export const PrintCardComponent = ({ order }) => {
  const formatTime = (timestamp) => {
    if (!timestamp) return '00:00 AM';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <PrintCardContainer>
      <PrintCardHeader>
        <div className="table-info">
          <span>Table {order.tableNumber}</span>
          <span className="timestamp">{formatTime(order.createdAt || order.timestamp)}</span>
        </div>
        <div className="order-number">{order.orderNumber}</div>
      </PrintCardHeader>

      <PrintCardItems>
        <div className="items-title">Items:</div>
        {order.items?.map((item, index) => (
          <div key={index}>
            <div className="item">
              <span className="qty-name">{item.name || item.itemName}</span>
              <span className="qty">x{item.quantity || 1}</span>
            </div>
            {(item.notes || item.specialInstructions) && (
              <div className="notes">
                {item.notes || item.specialInstructions}
              </div>
            )}
          </div>
        ))}
      </PrintCardItems>
    </PrintCardContainer>
  );
};

export default KOTDisplay;
